import { api } from './client';
import { fetchCompleteCollection } from './pagination';

type ProjectTaskFilters = {
  status?: string;
  assignee_id?: string;
  priority?: string;
  section_id?: string;
  search?: string;
};

type MyTaskFilters = {
  status?: string;
  due_date_from?: string;
  due_date_to?: string;
};

function getProjectTaskPage(
  projectId: string,
  params?: ProjectTaskFilters & { page?: number; limit?: number },
) {
  return api.get<{
    tasks: import('../types').Task[];
    pagination: import('../types').PaginationInfo;
  }>(`/tasks/project/${projectId}`, params);
}

function getMyTaskPage(params?: MyTaskFilters & { page?: number; limit?: number }) {
  return api.get<{
    tasks: import('../types').Task[];
    pagination: import('../types').PaginationInfo;
  }>('/tasks/my-tasks', params);
}

// Follow-up Reminder type
export interface FollowupReminder {
  id: string;
  task_id: string;
  created_by: string;
  created_by_name?: string;
  reminder_at: string;
  message: string;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

// Task Project type (for multi-homing)
export interface TaskProject {
  id: string;
  name: string;
  color: string;
  status: string;
  section_id?: string;
  section_name?: string;
  can_update?: boolean;
}

// Task Dependency type
export interface TaskDependency {
  id: string;
  dependency_type: 'blocks' | 'blocked_by';
  task_id: string;
  title: string;
  status: string;
  priority: string;
  created_by_name?: string;
  created_at: string;
  project_id?: string;
  project_name?: string;
  project_color?: string;
}

// Tasks API
export const tasksApi = {
  getByProject: getProjectTaskPage,

  getAllByProject: async (projectId: string, filters?: ProjectTaskFilters) => {
    const tasks = await fetchCompleteCollection<import('../types').Task>((page, limit) =>
      getProjectTaskPage(projectId, { ...filters, page, limit }).then((response) => ({
        items: response.tasks,
        pagination: response.pagination,
      })),
    );
    return {
      tasks,
      pagination: {
        page: 1,
        limit: tasks.length,
        total: tasks.length,
        totalPages: tasks.length ? 1 : 0,
      },
    };
  },

  getMyTasks: async (filters?: MyTaskFilters) => {
    const tasks = await fetchCompleteCollection<import('../types').Task>(
      (page, limit) =>
        getMyTaskPage({ ...filters, page, limit }).then((response) => ({
          items: response.tasks,
          pagination: response.pagination,
        })),
      { pageSize: 1000 },
    );
    return {
      tasks,
      pagination: {
        page: 1,
        limit: tasks.length,
        total: tasks.length,
        totalPages: tasks.length ? 1 : 0,
      },
    };
  },

  getById: (id: string) =>
    api.get<{
      task: import('../types').Task;
      projects: TaskProject[];
      comments: import('../types').Comment[];
      attachments: import('../types').Attachment[];
      customFields: import('../types').CustomField[];
      activity: import('../types').ActivityLog[];
      tags: import('../types').TaskTag[];
      watchers: import('../types').TaskWatcher[];
      checklist: import('../types').TaskChecklistItem[];
      permissions: import('../types').TaskPermissions;
    }>(`/tasks/${id}`),

  create: (data: {
    title: string;
    description?: string;
    project_id: string;
    section_id?: string;
    assignee_id: string;
    priority?: string;
    start_date?: string;
    due_date?: string;
    task_type?: import('../types').TaskType;
    time_estimate_minutes?: number;
    parent_task_id?: string;
    tags?: string[];
  }) => api.post<{ task: import('../types').Task }>('/tasks', data),

  getSubtasks: (taskId: string) =>
    api.get<{ subtasks: import('../types').Task[] }>(`/tasks/${taskId}/subtasks`),

  update: (id: string, data: Partial<import('../types').Task>) =>
    api.patch<{ task: import('../types').Task }>(`/tasks/${id}`, data),

  bulkUpdate: (
    taskIds: string[],
    updates: Partial<
      Pick<
        import('../types').Task,
        | 'status'
        | 'priority'
        | 'assignee_id'
        | 'start_date'
        | 'due_date'
        | 'task_type'
        | 'time_estimate_minutes'
        | 'completion_comment'
      >
    >,
  ) =>
    api.patch<{
      updated: number;
      task_ids: string[];
      tasks: import('../types').Task[];
    }>('/tasks/bulk', {
      task_ids: taskIds,
      updates,
    }),

  move: (id: string, data: { project_id: string; section_id?: string | null; position?: number }) =>
    api.patch<{ message: string; task: import('../types').Task }>(`/tasks/${id}/move`, data),

  delete: (id: string) => api.delete<{ message: string }>(`/tasks/${id}`),

  addComment: (taskId: string, content: string, parentCommentId?: string) =>
    api.post<{ comment: import('../types').Comment }>(`/tasks/${taskId}/comments`, {
      content,
      parent_comment_id: parentCommentId,
    }),

  updateCustomField: (taskId: string, fieldId: string, value: string) =>
    api.patch(`/tasks/${taskId}/custom-fields/${fieldId}`, { value }),

  batchUpdateCustomFields: (
    taskId: string,
    fields: Array<{ fieldId: string; value: string | number | null }>,
  ) =>
    api.put<{ message: string; updated: number }>(`/tasks/${taskId}/custom-fields/batch`, {
      fields,
    }),

  // Dependencies
  getDependencies: (taskId: string) =>
    api.get<{
      blockedBy: TaskDependency[];
      blocks: TaskDependency[];
    }>(`/tasks/${taskId}/dependencies`),

  addDependency: (
    taskId: string,
    blockingTaskId: string,
    type: 'blocks' | 'blocked_by' = 'blocked_by',
  ) =>
    api.post<{ dependency: TaskDependency }>(`/tasks/${taskId}/dependencies`, {
      blocking_task_id: blockingTaskId,
      dependency_type: type,
    }),

  removeDependency: (taskId: string, dependencyId: string) =>
    api.delete<{ message: string }>(`/tasks/${taskId}/dependencies/${dependencyId}`),

  // Multi-homing (task in multiple projects)
  getTaskProjects: (taskId: string) =>
    api.get<{
      projects: TaskProject[];
    }>(`/tasks/${taskId}/projects`),

  addToProject: (taskId: string, projectId: string, sectionId?: string) =>
    api.post<{ message: string; project: { id: string; name: string; color: string } }>(
      `/tasks/${taskId}/projects`,
      { project_id: projectId, section_id: sectionId },
    ),

  removeFromProject: (taskId: string, projectId: string) =>
    api.delete<{ message: string }>(`/tasks/${taskId}/projects/${projectId}`),

  // Real-time collaboration - Kick all users from task session (admin only)
  kickAllUsers: (taskId: string) =>
    api.post<{ message: string; kicked: number }>(`/realtime/tasks/${taskId}/kick-all`, {}),

  // Follow-up reminders
  getFollowups: (taskId: string) =>
    api.get<{ reminders: FollowupReminder[] }>(`/tasks/${taskId}/followups`),

  createFollowup: (taskId: string, data: { reminder_at: string; message: string }) =>
    api.post<{ reminder: FollowupReminder }>(`/tasks/${taskId}/followups`, data),

  deleteFollowup: (taskId: string, reminderId: string) =>
    api.delete<{ message: string }>(`/tasks/${taskId}/followups/${reminderId}`),

  getTags: (taskId: string) =>
    api.get<{ tags: import('../types').TaskTag[] }>(`/tasks/${taskId}/tags`),

  replaceTags: (taskId: string, tags: string[]) =>
    api.put<{ tags: import('../types').TaskTag[] }>(`/tasks/${taskId}/tags`, { tags }),

  getWatchers: (taskId: string) =>
    api.get<{ watchers: import('../types').TaskWatcher[] }>(`/tasks/${taskId}/watchers`),

  addWatcher: (taskId: string, userId?: string) =>
    api.post<{ watchers: import('../types').TaskWatcher[] }>(`/tasks/${taskId}/watchers`, {
      ...(userId ? { user_id: userId } : {}),
    }),

  removeWatcher: (taskId: string, userId: string) =>
    api.delete<{ message: string }>(`/tasks/${taskId}/watchers/${userId}`),

  getChecklist: (taskId: string) =>
    api.get<{ items: import('../types').TaskChecklistItem[] }>(`/tasks/${taskId}/checklist`),

  createChecklistItem: (
    taskId: string,
    data: { title: string; assignee_id?: string | null; position?: number },
  ) => api.post<{ item: import('../types').TaskChecklistItem }>(`/tasks/${taskId}/checklist`, data),

  updateChecklistItem: (
    taskId: string,
    itemId: string,
    data: Partial<
      Pick<import('../types').TaskChecklistItem, 'title' | 'assignee_id' | 'position'>
    > & { is_completed?: boolean },
  ) =>
    api.patch<{ item: import('../types').TaskChecklistItem }>(
      `/tasks/${taskId}/checklist/${itemId}`,
      data,
    ),

  deleteChecklistItem: (taskId: string, itemId: string) =>
    api.delete<{ message: string }>(`/tasks/${taskId}/checklist/${itemId}`),
};
